import { useEffect, useRef, useState } from "react";

// Subtle luxury circular cursor with smooth trailing dot.
// Grows over links/buttons; auto-disabled on touch devices.
const CustomCursor = () => {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const target = useRef({ x: -100, y: -100 });
  const pos = useRef({ x: -100, y: -100 });
  const [enabled, setEnabled] = useState(false);
  const [hover, setHover] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    setEnabled(canHover);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      if (!visible) setVisible(true);
    };
    const onDown = () => setPressed(true);
    const onUp = () => setPressed(false);
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    const interactiveSelector =
      'a, button, [role="button"], input, textarea, select, label, [data-cursor="link"]';

    const onOver = (e) => {
      if (e.target.closest && e.target.closest(interactiveSelector)) setHover(true);
    };
    const onOut = (e) => {
      if (e.target.closest && e.target.closest(interactiveSelector)) setHover(false);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);

    let raf;
    const loop = () => {
      // Ring trails smoothly; dot follows immediately for a considered feel.
      pos.current.x += (target.current.x - pos.current.x) * 0.18;
      pos.current.y += (target.current.y - pos.current.y) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${pos.current.x}px, ${pos.current.y}px, 0) translate(-50%, -50%)`;
      }
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${target.current.x}px, ${target.current.y}px, 0) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
    };
  }, [enabled, visible]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={ringRef}
        className={`odda-cursor odda-cursor--ring ${hover ? "is-hover" : ""} ${pressed ? "is-pressed" : ""} ${visible ? "is-visible" : ""}`}
        aria-hidden="true"
      />
      <div
        ref={dotRef}
        className={`odda-cursor odda-cursor--dot ${hover ? "is-hover" : ""} ${visible ? "is-visible" : ""}`}
        aria-hidden="true"
      />
    </>
  );
};

export default CustomCursor;
